/**
 * The better-auth session and auth actions, unified into the app's React
 * Query cache — the dashboard's only cache. Mutations invalidate the session
 * (and me.tenants where membership/role-driven nav depends on it) so sign-in
 * and invite-accept resolve only once the cache reflects them; no more
 * racing better-auth's own store.
 */

import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authClient } from "./auth";
import { useTRPC } from "./trpc";

export const sessionKey = ["auth", "session"] as const;

type SessionData = Awaited<ReturnType<typeof authClient.getSession>>["data"];

// Id only, never the email — the PII policy.
function applySentryUser(session: SessionData | null) {
  Sentry.setUser(session ? { id: session.user.id } : null);
}

export function useSessionQuery() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: async (): Promise<SessionData | null> => {
      const { data, error } = await authClient.getSession();
      if (error) throw new Error(error.message ?? "session check failed");
      // Signed out must be null — React Query rejects undefined resolutions.
      applySentryUser(data ?? null);
      return data ?? null;
    },
    staleTime: 60_000,
    // Override the app defaults (retry: 1, no focus refetch): auth failures
    // shouldn't double-hit, and a focus refetch is how a sign-out in another
    // tab reaches this one.
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useSignInMutation() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  return useMutation({
    mutationFn: async (params: { email: string; password: string }) => {
      const { error } = await authClient.signIn.email(params);
      if (error) throw new Error(error.message ?? "sign in failed");
    },
    onSuccess: async () => {
      // Awaited so mutateAsync resolves only once the session (and the
      // role-dependent nav data) is in cache — the redirect can't bounce.
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      await queryClient.invalidateQueries({ queryKey: trpc.me.tenants.pathKey() });
    },
  });
}

/**
 * Asks Better Auth to email a reset link. Its body is the same whether or not the email
 * has an account, so success says nothing about the address. Sends no `redirectTo`: the
 * server builds the link, and Better Auth refuses a `redirectTo` on another origin.
 */
export function useRequestPasswordResetMutation() {
  return useMutation({
    mutationFn: async (params: { email: string }) => {
      const { error } = await authClient.requestPasswordReset(params);
      if (error) throw new Error(error.message ?? "could not send the reset link");
    },
  });
}

/** A refused auth call that keeps Better Auth's error code, so a page can tell its causes apart. */
export class AuthCallError extends Error {
  readonly code: string | undefined;

  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = "AuthCallError";
    this.code = code;
  }
}

/** True when Better Auth refused a reset link's token: expired, already used, or unknown. */
export function isRefusedResetLink(error: unknown): boolean {
  return error instanceof AuthCallError && error.code === "INVALID_TOKEN";
}

/**
 * Sets a new password from the token in a reset link. Better Auth uses the token once and
 * signs out every session of the user, so the cached session is refetched after it. The
 * reset page shows a refused link itself, so that failure gets no toast.
 */
export function useResetPasswordMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { showsError: isRefusedResetLink },
    mutationFn: async (params: { token: string; newPassword: string }) => {
      const { error } = await authClient.resetPassword(params);
      if (error)
        throw new AuthCallError(error.message ?? "could not reset the password", error.code);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionKey });
    },
  });
}

export function useSignUpMutation() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  return useMutation({
    mutationFn: async (params: { email: string; name: string; password: string }) => {
      const { error } = await authClient.signUp.email(params);
      if (error) throw new Error(error.message ?? "could not create the account");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      await queryClient.invalidateQueries({ queryKey: trpc.me.tenants.pathKey() });
    },
  });
}

export function useSignOutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await authClient.signOut();
    },
    onSuccess: () => {
      // Drop everything, then seed the signed-out session so guards flip to
      // /login without a Loading flash or a doomed refetch.
      queryClient.clear();
      queryClient.setQueryData(sessionKey, null);
      applySentryUser(null);
    },
  });
}

export function useAcceptInviteMutation() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await authClient.organization.acceptInvitation({ invitationId });
      if (error) throw new Error(error.message ?? "could not accept the invitation");
    },
    onSuccess: async () => {
      // Membership changed: TenantLayout must not render from stale me.tenants.
      await queryClient.invalidateQueries({ queryKey: trpc.me.tenants.pathKey() });
      await queryClient.invalidateQueries({ queryKey: sessionKey });
    },
  });
}
